import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2, Receipt } from "lucide-react";
import api from "@/lib/api";
import { cadStr } from "@/lib/pricing";
import { KeepPriceNotice } from "@/components/merchant/billing/KeepPriceNotice";

const fmt = (iso) => (iso && !Number.isNaN(Date.parse(iso))
  ? new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : iso);

function PendingDowngrade({ pending, planName, onCancelled }) {
  const [busy, setBusy] = useState(false);
  if (!pending || pending.kind !== "downgrade") return null;
  const cancel = async () => {
    try {
      setBusy(true);
      await api.post("/merchant/plan/cancel-downgrade");
      toast.success("Scheduled change cancelled.");
      onCancelled();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel the scheduled change.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-3"
         data-testid="plan-pending-downgrade">
      <div className="inline-flex items-center gap-2">
        <CalendarClock className="w-4 h-4" />
        Moving to <strong className="mx-1">{planName(pending.to_plan)}</strong> on <strong className="ml-1">{fmt(pending.effective_at)}</strong>
        — you keep your current plan until then.
      </div>
      <button onClick={cancel} disabled={busy} data-testid="plan-cancel-downgrade-btn"
              className="text-xs font-semibold underline hover:text-amber-950 disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Keep my current plan"}
      </button>
    </div>
  );
}

export function PlanSummaryCard({ catalog, refreshKey, onChanged }) {
  const [s, setS] = useState(null);
  const load = useCallback(() => {
    api.get("/merchant/plan-summary").then((r) => setS(r.data)).catch(() => setS(null));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!s) return null;
  const planName = (id) => catalog?.plans?.find((p) => p.id === id)?.name || id;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="plan-summary-card">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold inline-flex items-center gap-2">
        <Receipt className="w-3.5 h-3.5" /> Your pricing
      </div>
      <div className="mt-3 grid sm:grid-cols-3 gap-4">
        <div>
          <div className="text-[11px] text-slate-500">Plan</div>
          <div className="font-display text-xl text-slate-900" data-testid="plan-summary-plan">{s.plan_label}</div>
          <div className="text-xs text-slate-500" data-testid="plan-summary-interval">
            {s.billing_interval ? `${s.billing_interval === "annual" ? "Annual" : "Monthly"} billing` : "No subscription yet"}
          </div>
        </div>
        <div>
          <div className="text-[11px] text-slate-500">Subscription</div>
          <div className="font-display text-xl text-slate-900" data-testid="plan-summary-price">
            {s.subscription_cad ? `${cadStr(s.subscription_cad)}/${s.subscription_period}` : "—"}
          </div>
        </div>
        <div>
          <div className="text-[11px] text-slate-500">Performance fee per verified sale</div>
          <div className="font-display text-xl text-slate-900" data-testid="plan-summary-fee">
            {s.fee_pct ? `${s.fee_pct}% or ${cadStr(s.fee_minimum_cad)}` : "—"}
          </div>
          <div className="text-xs text-slate-500">whichever is higher</div>
        </div>
      </div>
      {s.plan_effective_at && (
        <div className="mt-3 text-xs text-slate-500" data-testid="plan-summary-effective">Current plan since {fmt(s.plan_effective_at)}</div>
      )}
      <KeepPriceNotice gf={s.grandfathered} />
      <PendingDowngrade pending={s.pending_plan_change} planName={planName} onCancelled={() => { load(); onChanged?.(); }} />
    </div>
  );
}
